import { supabase } from './supabase';
import type { Database } from '../types/database.types';
import { logger } from '../utils/logger';

type User = Database['public']['Tables']['users']['Row'];
type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];

export interface UserWithRelations extends User {
  company?: Company | null;
  branch?: Branch | null;
}

export interface UserFilters {
  companyId?: string;
  branchId?: string;
  isActive?: boolean;
  searchTerm?: string;
}

export class UserService {
  // 사용자 목록 조회 (관계 데이터 포함)
  static async getUsers(filters?: UserFilters) {
    try {
      let query = supabase
        .from('users')
        .select(`
          *,
          company:companies(id, name, company_code),
          branch:branches(id, branch_name, branch_code)
        `)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      if (filters?.companyId) {
        query = query.eq('company_id', filters.companyId);
      }

      if (filters?.branchId) {
        query = query.eq('branch_id', filters.branchId);
      }

      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }

      if (filters?.searchTerm) {
        query = query.or(`name.ilike.%${filters.searchTerm}%,username.ilike.%${filters.searchTerm}%,phone_number.ilike.%${filters.searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as UserWithRelations[];
    } catch (error) {
      logger.error('Error fetching users', error);
      throw error;
    }
  }

  // 단일 사용자 조회
  static async getUser(userId: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          company:companies(id, name, company_code),
          branch:branches(id, branch_name, branch_code)
        `)
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data as UserWithRelations;
    } catch (error) {
      logger.error('Error fetching user', error, { id: userId });
      throw error;
    }
  }

  // 사용자 생성
  static async createUser(userData: {
    username: string;
    name: string;
    password: string;
    companyId?: string;
    branchId?: string;
    phoneNumber?: string;
    isAdmin?: boolean;
    hasAdminRole?: boolean;
  }) {
    try {
      // 1. Auth 사용자 생성 (signUp 사용)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: `${userData.username}@tbm.app`,
        password: userData.password,
      });

      if (authError) throw authError;

      // 2. Public 사용자 생성
      const { data, error } = await supabase
        .from('users')
        .insert({
          username: userData.username,
          name: userData.name,
          company_id: userData.companyId,
          branch_id: userData.branchId,
          phone_number: userData.phoneNumber,
          is_admin: userData.isAdmin || false,
          has_admin_role: userData.hasAdminRole || false,
          auth_id: authData.user?.id,
          is_active: true,
          is_first_login: true
        })
        .select()
        .single();

      if (error) {
        // Auth 사용자 삭제 (롤백) - signUp으로 생성한 경우 admin 권한 없이는 삭제 불가
        logger.error('Failed to create user in users table', error, { userData });
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error creating user', error);
      throw error;
    }
  }

  // 사용자 정보 수정
  static async updateUser(userId: string, userData: {
    name?: string;
    companyId?: string;
    branchId?: string;
    phoneNumber?: string;
    isActive?: boolean;
    hasAdminRole?: boolean;
  }) {
    try {
      const updateData: any = {};
      
      if (userData.name !== undefined) updateData.name = userData.name;
      if (userData.companyId !== undefined) updateData.company_id = userData.companyId;
      if (userData.branchId !== undefined) updateData.branch_id = userData.branchId;
      if (userData.phoneNumber !== undefined) updateData.phone_number = userData.phoneNumber;
      if (userData.isActive !== undefined) updateData.is_active = userData.isActive;
      if (userData.hasAdminRole !== undefined) updateData.has_admin_role = userData.hasAdminRole;

      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating user', error, { id: userId });
      throw error;
    }
  }

  // 사용자 삭제 (소프트 삭제)
  static async deleteUser(userId: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ 
          is_deleted: true,
          is_active: false,
          deletion_scheduled_date: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error deleting user', error, { id: userId });
      throw error;
    }
  }

  // 비밀번호 초기화 (핸드폰번호로 초기화)
  static async resetPassword(userId: string) {
    try {
      // 사용자 정보 가져오기
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('auth_id, username, phone_number')
        .eq('id', userId)
        .single();

      if (userError || !userData) throw userError || new Error('User not found');

      // Edge Function을 통한 비밀번호 초기화
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          userId: userData.auth_id,
          newPassword: userData.username // 핸드폰번호로 초기화
        }
      });

      if (error) throw error;

      return { 
        success: true,
        message: `${userData.username} 사용자의 비밀번호가 핸드폰번호로 초기화되었습니다.`
      };
    } catch (error) {
      logger.error('Error resetting password', error, { id: userId });
      throw error;
    }
  }

  // 회사 목록 조회
  static async getCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error fetching companies', error);
      throw error;
    }
  }

  // 지점 목록 조회
  static async getBranches(companyId?: string) {
    try {
      let query = supabase
        .from('branches')
        .select('*')
        .order('branch_name');

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error fetching branches', error, { companyId });
      throw error;
    }
  }

  // 사용자-지점 관계 업데이트
  static async updateUserBranches(userId: string, branchIds: string[]) {
    try {
      // 기존 관계 삭제
      await supabase
        .from('user_branches')
        .delete()
        .eq('user_id', userId);

      // 새로운 관계 추가
      if (branchIds.length > 0) {
        const relations = branchIds.map(branchId => ({
          user_id: userId,
          branch_id: branchId
        }));

        const { error } = await supabase
          .from('user_branches')
          .insert(relations);

        if (error) throw error;
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating user branches', error, { userId, branchIds });
      throw error;
    }
  }

  // 사용자의 지점 목록 조회
  static async getUserBranches(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_branches')
        .select('branch_id')
        .eq('user_id', userId);

      if (error) throw error;
      return data.map(item => item.branch_id);
    } catch (error) {
      logger.error('Error fetching user branches', error, { userId });
      throw error;
    }
  }
}